import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { api } from '../../lib/api';
import type { Post, Comment } from '../../lib/types';
import { BlogLayout } from '../../components/BlogLayout';
import { PostBodyRenderer } from '../../components/PostBodyRenderer';
import { CommentsSection } from '../../components/CommentsSection';
import { PageHeader } from '../../components/PageHeader';
import { slugifyTag } from '../../lib/tagSlug';

const fallbackAvatar =
  'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80';

function formatDateWithYear(dateString?: string | null) {
  if (!dateString) return '';
  const d = new Date(dateString);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getAuthorAvatar(author?: Post['author']) {
  return author?.avatar_url || fallbackAvatar;
}

function getAuthorTopics(author?: Post['author'], category?: string | null) {
  if (author?.topics?.length) {
    return author.topics.slice(0, 3);
  }
  if (category) {
    return [category];
  }
  return ['Insights'];
}

export default function PostDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  useEffect(() => {
    async function fetchPost() {
      if (!slug) return;

      try {
        const [postResponse, commentsResponse] = await Promise.all([
          api.get<Post>(`/api/v1/posts/${slug}`),
          api.get<Comment[]>(`/api/v1/posts/${slug}/comments`)
        ]);

        if (postResponse.success && postResponse.data) {
          setPost(postResponse.data);
        } else {
          setError(postResponse.error?.message || 'Post not found');
        }

        if (commentsResponse.success && commentsResponse.data) {
          // Filter to only show approved comments
          setComments(commentsResponse.data.filter(c => c.status === 'approved'));
        }
      } catch (err) {
        setError('Failed to load post');
        console.error('Error fetching post:', err);
      } finally {
        setLoading(false);
      }
    }

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <BlogLayout>
        <Helmet>
          <title>Loading... - QuestFlow</title>
        </Helmet>
        <div className="page-body">
          <PageHeader title="Loading post" eyebrow="Questflow" />
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p className="placeholder">Loading post...</p>
          </div>
        </div>
      </BlogLayout>
    );
  }

  if (error || !post) {
    return (
      <BlogLayout>
        <Helmet>
          <title>Not Found - QuestFlow</title>
        </Helmet>
        <div className="page-body">
          <PageHeader title="Post not found" eyebrow="Questflow" />
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <p className="placeholder" style={{ color: '#ef4444' }}>
              {error || 'Post not found'}
            </p>
            <Link to="/" className="inline-link" style={{ marginTop: '20px', display: 'inline-block' }}>
              Back to home
            </Link>
          </div>
        </div>
      </BlogLayout>
    );
  }

  const authorName = post.author?.name || 'Questflow Desk';
  const authorTitle = post.author?.title || 'Staff Writer';
  const authorBio =
    post.author?.bio ||
    'Covers automation, testing, and reliability so Questflow readers get crisp, trustworthy picks.';
  const authorTopics = getAuthorTopics(post.author, post.category);
  const commentCount = comments.length;
  const tags = post.tags || [];

  return (
    <BlogLayout>
      <Helmet>
        <title>{post.title} - QuestFlow</title>
        <meta name="description" content={post.excerpt || post.title} />
        {post.header_image_url && <meta property="og:image" content={post.header_image_url} />}
      </Helmet>
      <div className="page-body">
        <article className="article-page upgraded">
          <div className="article-topline">
            {tags.length ? (
              <div className="tag-row">
                {tags.map((tag) => {
                  const label = tag as string;
                  const slug = slugifyTag(label);
                  const href = slug ? `/tag/${slug}` : '/archive';
                  return (
                    <Link key={label} to={href} className="tag-pill ghost">
                      {label}
                    </Link>
                  );
                })}
              </div>
            ) : null}

            <PageHeader
              title={post.title}
              eyebrow={post.category || 'Post'}
              titleClassName="post-page-title"
            />
          </div>

          <div className="article-layout">
            <div className="article-main">
              <div className="article-meta">
                <div className="byline">
                  <span className="author-name">{authorName}</span>
                  <span className="dot-sep" />
                  <span>{formatDateWithYear(post.date_published || post.created_at)}</span>
                </div>
                <div>
                  <a className="comment-count" href="#comments">
                    {commentCount === 1 ? '1 comment' : `${commentCount} comments`}
                  </a>
                </div>
              </div>

              {post.header_image_url && (
                <div className="article-hero">
                  <img
                    src={post.header_image_url}
                    alt={post.title}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </div>
              )}

              {post.excerpt ? <p className="hero-excerpt">{post.excerpt}</p> : null}

              <PostBodyRenderer html={post.body} className="article-body" />

              <CommentsSection
                postId={post.id}
                initialComments={comments}
                turnstileSiteKey={turnstileSiteKey}
              />
            </div>

            <aside className="article-aside">
              <div className="author-card">
                <div className="author-card-visual">
                  <img
                    src={getAuthorAvatar(post.author)}
                    alt={authorName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 25%' }}
                  />
                </div>
                <div className="author-card-name-overlay">
                  <div className="author-card-name-display">{authorName}</div>
                  <div className="author-card-role-display">{authorTitle}</div>
                </div>
                <p className="author-card-bio">{authorBio}</p>
                {authorTopics.length ? (
                  <div className="author-beats">
                    {authorTopics.map((beat) => (
                      <span key={beat} className="author-beat-chip">
                        {beat}
                      </span>
                    ))}
                  </div>
                ) : null}
                <div className="author-card-footer">
                  <div className="author-card-label">Latest coverage</div>
                  <p className="author-card-note">
                    Expect sharp, fast takes on {authorTopics.join(', ')} with clear picks and links you can act on.
                  </p>
                </div>
              </div>
            </aside>
          </div>
        </article>
      </div>
    </BlogLayout>
  );
}
